/**
 * @citeclerk/ui — PurchaseResultModal.jsx. RUN PX, Task 4.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ONE modal for the outcome of a purchase — success or failure — used by BOTH the subscription
 * checkout (apps/main/src/checkout) and the à la carte sheet (CardSheet.jsx). Before this, à la
 * carte completed silently (no confirmation at all) and a subscription flashed a success screen
 * and REDIRECTED to the homescreen. Adam: "That's BAD." Two different endings for the same kind of
 * event is how a customer ends up unsure whether something happened; this is the one place either
 * flow says so.
 *
 * 🔴 DISMISSED ONLY BY ITS X. No scrim-click-to-close, no Escape. A completed-or-failed purchase is
 * a fact the customer should have to actively acknowledge, not one that vanishes if their cursor
 * strays off the panel — and unlike the CARD-ENTRY sheets (CardSheet, the subscription form), there
 * is no live payment in flight here to protect from an accidental dismiss either way.
 *
 * 🔴 NEVER NAVIGATES. It has no `navigate`/`href` anywhere in it. The caller is responsible for
 * having already done whatever finalizing work it needed (Clerk's `checkout.finalize()`, the pack
 * deposit) BEFORE opening this — this component only reports the outcome.
 *
 * Styled with the same navy-header / DM Serif Display idiom CardSheet.jsx and the checkout modal
 * already use, so all three read as one product. RUN PX AMENDMENT — carries `LegalLinks` on both
 * faces: this is the screen that shows the outcome of taking money, which the amendment names
 * explicitly alongside the two checkouts.
 */
import React, { useEffect, useRef } from 'react';
import LegalLinks from './LegalLinks.jsx';

const CSS = `
.cc-prm-root{position:fixed;inset:0;z-index:10001}
.cc-prm-scrim{position:absolute;inset:0;background:color-mix(in srgb, var(--cc-chrome) 62%, transparent);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.cc-prm-panel{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:min(440px,94vw);max-height:92vh;overflow-y:auto;
  background:var(--cc-card,#fff);border-radius:16px;box-shadow:var(--cc-ia-shadow-modal);
  display:flex;flex-direction:column;padding:0 0 30px;font-family:'DM Sans',system-ui,sans-serif}
.cc-prm-header{background:var(--cc-chrome,#002368);padding:24px 24px 30px;border-radius:16px 16px 0 0;position:relative;flex-shrink:0}
.cc-prm-close{position:absolute;top:14px;right:14px;width:30px;height:30px;border:.5px solid var(--cc-on-chrome-line,rgba(255,255,255,.25));
  border-radius:8px;background:var(--cc-on-chrome-fill);color:var(--cc-on-chrome-soft,rgba(255,255,255,.8));font-size:14px;cursor:pointer;
  display:flex;align-items:center;justify-content:center}
.cc-prm-close:hover{background:var(--cc-on-chrome-fill-hover);color:var(--cc-on-chrome,#fff)}
.cc-prm-close:focus-visible{outline:none;box-shadow:0 0 0 2px var(--cc-focus-halo-light,rgba(255,255,255,.55)),0 0 0 5px var(--cc-focus-halo-dark,rgba(255,255,255,.2))}
.cc-prm-icon{width:52px;height:52px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:4px auto 16px;color:var(--cc-on-chrome,#fff)}
.cc-prm-icon--ok{background:var(--cc-on-chrome-fill)}
.cc-prm-icon--fail{background:var(--cc-on-chrome-fill)}
.cc-prm-eyebrow{font-size:11px;letter-spacing:.11em;text-transform:uppercase;color:var(--cc-on-chrome-faint,rgba(255,255,255,.62));font-weight:700;text-align:center}
.cc-prm-title{font-family:'DM Serif Display',Georgia,serif;font-size:22px;font-weight:400;color:var(--cc-on-chrome,#fff);margin:6px 0 0;line-height:1.2;text-align:center}
.cc-prm-body{display:flex;flex-direction:column;gap:10px;padding:24px 28px 4px;border-top:2px solid var(--cc-brand-a12)}
.cc-prm-line{font-size:14.5px;line-height:1.55;color:var(--cc-ink,#141a26);text-align:center;margin:0}
.cc-prm-line--muted{color:var(--cc-ink-muted,#6B86B5);font-size:13px}
`;
let _css = false;
function usePrmCss() {
  useEffect(() => {
    if (_css || typeof document === 'undefined') return;
    const el = document.createElement('style'); el.dataset.ccPrm = '1'; el.textContent = CSS;
    document.head.appendChild(el); _css = true;
  }, []);
}

function OkIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function FailIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {function} props.onClose     the X. The only way this closes.
 * @param {'success'|'failure'} props.outcome
 * @param {string}   props.eyebrow     small label above the title, e.g. "Subscription" / "À la carte"
 * @param {string}   props.title       e.g. "You're subscribed" / "We couldn't complete that purchase"
 * @param {string[]} [props.lines]     body sentences — what was bought / what it granted, or the reason
 */
export default function PurchaseResultModal({ open, onClose, outcome, eyebrow, title, lines = [] }) {
  usePrmCss();
  const closeRef = useRef(null);
  const ok = outcome !== 'failure';

  // Focus the X on open so a keyboard user lands somewhere reachable immediately — this modal
  // takes no other input, so there is nowhere else focus belongs.
  useEffect(() => {
    if (open && closeRef.current) closeRef.current.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="cc-prm-root" role="dialog" aria-modal="true" aria-labelledby="cc-prm-title">
      {/* No onClick here — dismissed only by the X, per Adam's rule. */}
      <div className="cc-prm-scrim" aria-hidden="true" />
      <div className="cc-prm-panel">
        <div className="cc-prm-header">
          <button type="button" ref={closeRef} className="cc-prm-close" aria-label="Close" onClick={onClose}>✕</button>
          <div className={"cc-prm-icon " + (ok ? "cc-prm-icon--ok" : "cc-prm-icon--fail")}>
            {ok ? <OkIcon /> : <FailIcon />}
          </div>
          {eyebrow && <div className="cc-prm-eyebrow">{eyebrow}</div>}
          <h2 className="cc-prm-title" id="cc-prm-title">{title}</h2>
        </div>
        <div className="cc-prm-body">
          {lines.map((l, i) => <p key={i} className={"cc-prm-line" + (i > 0 ? " cc-prm-line--muted" : "")}>{l}</p>)}
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
